import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Shield, Lock, Eye, Database, Mail, Globe } from "lucide-react";

export default function PrivacyPolicyPage() {
  const navigate = useNavigate();
  const lastUpdated = "March 5, 2026";

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Privacy Policy</h1>
            <p className="text-sm text-gray-500">Last updated: {lastUpdated}</p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-sm p-8 space-y-8">
          
          {/* Introduction */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-violet-100 rounded-lg">
                <Shield className="w-6 h-6 text-violet-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Introduction</h2>
            </div>
            <p className="text-gray-600 leading-relaxed">
              Welcome to MarkeeMix ("we," "our," or "us"). We are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website, services, and Chrome extension.
            </p>
          </section>

          {/* Information We Collect */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Database className="w-6 h-6 text-blue-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Information We Collect</h2>
            </div>
            
            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Personal Information</h3>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
              <li>Name and email address (when you register)</li>
              <li>Phone number (optional, for WhatsApp notifications)</li>
              <li>Payment information (processed securely via Stripe)</li>
              <li>Google account information (if using Google Sign-In)</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Usage Information</h3>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
              <li>URLs analyzed through our SEO tools</li>
              <li>Search queries and tool usage patterns</li>
              <li>Device information and browser type</li>
              <li>IP address and approximate location</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Chrome Extension Data</h3>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
              <li>Current page URL (only when you click the extension)</li>
              <li>SEO analysis results (stored locally on your device)</li>
              <li>We do NOT collect browsing history or personal data from the extension</li>
            </ul>
          </section>

          {/* How We Use Information */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-green-100 rounded-lg">
                <Eye className="w-6 h-6 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">How We Use Your Information</h2>
            </div>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
              <li>Provide and improve our SEO analysis services</li>
              <li>Process payments and manage subscriptions</li>
              <li>Send transactional emails (welcome, payment confirmations)</li>
              <li>Send marketing communications (with your consent)</li>
              <li>Analyze usage patterns to improve our tools</li>
              <li>Prevent fraud and ensure security</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          {/* Data Security */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Lock className="w-6 h-6 text-yellow-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Data Security</h2>
            </div>
            <p className="text-gray-600 leading-relaxed">
              We implement industry-standard security measures to protect your data:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4 mt-2">
              <li>SSL/TLS encryption for all data transmission</li>
              <li>Secure password hashing (bcrypt)</li>
              <li>Regular security audits and updates</li>
              <li>Limited employee access to personal data</li>
              <li>Secure payment processing via Stripe (PCI compliant)</li>
            </ul>
          </section>

          {/* Third-Party Services */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Globe className="w-6 h-6 text-purple-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Third-Party Services</h2>
            </div>
            <p className="text-gray-600 leading-relaxed mb-2">
              We use the following third-party services:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
              <li><strong>Google OAuth:</strong> For secure authentication</li>
              <li><strong>Stripe:</strong> For payment processing</li>
              <li><strong>Resend:</strong> For transactional emails</li>
              <li><strong>MongoDB:</strong> For data storage</li>
              <li><strong>OpenAI:</strong> For AI-powered content generation</li>
            </ul>
            <p className="text-gray-600 mt-2">
              Each service has its own privacy policy governing their use of your data.
            </p>
          </section>

          {/* Your Rights */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Your Rights</h2>
            <p className="text-gray-600 leading-relaxed mb-2">You have the right to:</p>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
              <li>Access your personal data</li>
              <li>Correct inaccurate data</li>
              <li>Delete your account and data</li>
              <li>Export your data</li>
              <li>Opt-out of marketing communications</li>
              <li>Withdraw consent at any time</li>
            </ul>
          </section>

          {/* Cookies */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Cookies</h2>
            <p className="text-gray-600 leading-relaxed">
              We use essential cookies for authentication and session management. We do not use tracking cookies or sell your data to advertisers. You can disable cookies in your browser settings, but some features may not work properly.
            </p>
          </section>

          {/* Children's Privacy */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Children's Privacy</h2>
            <p className="text-gray-600 leading-relaxed">
              Our services are not intended for children under 13 years of age. We do not knowingly collect personal information from children. If you believe we have collected data from a child, please contact us immediately.
            </p>
          </section>

          {/* Changes to Policy */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Changes to This Policy</h2>
            <p className="text-gray-600 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any significant changes by posting the new policy on this page and updating the "Last updated" date. We encourage you to review this policy periodically.
            </p>
          </section>

          {/* Contact */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-pink-100 rounded-lg">
                <Mail className="w-6 h-6 text-pink-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Contact Us</h2>
            </div>
            <p className="text-gray-600 leading-relaxed">
              If you have any questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-gray-700"><strong>Email:</strong> contact@markeemix.com</p>
              <p className="text-gray-700"><strong>Phone:</strong> +91 8269911081</p>
              <p className="text-gray-700"><strong>Address:</strong> Indore, MP, India</p>
            </div>
          </section>

        </div>

        {/* Back Button */}
        <div className="text-center mt-8">
          <Button onClick={() => navigate("/")} variant="outline">
            Back to Home
          </Button>
        </div>
      </div>
    </div>
  );
}
